import LabelledElement from "./LabelledElement";
import { ErrorRounded as WarningIcon } from "@mui/icons-material";
import {
  Checkbox,
  List,
  ListItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { hasItems } from "common/arrays";
import { currencyFormat, dpString } from "common/numbers";
import StatusBadge from "components/common/StatusBadgeV2";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as userSelectors from "store/selectors/user/userSelectors";

const LayersSummary = ({
  data,
  quote,
  boundLayerId,
  selectable = false,
  onChange,
}) => {
  const theme = useTheme();
  const isFormsAdmin = useSelector(userSelectors.isFormsAdminUser);
  const hasPricing = quote?.premium != null;
  const isBound = boundLayerId != null;

  const referralReasons = isFormsAdmin
    ? Object.fromEntries(
        Object.entries(quote?.pricing?.values?.layers ?? {}).map(([k, v]) => [
          k,
          v.referralReasons,
        ])
      )
    : {};

  const memoisedLayers = useMemo(() => {
    let layers = hasItems(data.layers)
      ? data.layers
      : [
          {
            id: null,
            limit: data.limit,
            aggregate: data.aggregate,
            deductible: data.deductible,
          },
        ];

    if (hasPricing) {
      layers = layers.map((layer) => ({
        ...layer,
        premium:
          quote.premium[layer.id] ??
          (typeof quote.premium === "number" ? quote.premium : null),
      }));
    }

    return layers;
  }, [data, quote, hasPricing]);

  const [_selectedLayerId, setSelectedLayerId] = useState(null);

  const selectedLayerId = useMemo(
    () => _selectedLayerId ?? memoisedLayers?.[0]?.id ?? null,
    [_selectedLayerId, memoisedLayers]
  );

  const updateLayerId = useCallback(
    (id) => {
      if (!selectable) return;
      setSelectedLayerId(id);
      typeof onChange === "function" &&
        onChange(memoisedLayers.find((x) => x.id === id));
    },
    [selectable, onChange, memoisedLayers]
  );

  const dpFormatter = dpString(2, { prefix: "$" });

  return (
    <LabelledElement label={"Proposed Options"}>
      <TableContainer component={Paper} variant={"outlined"}>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              {selectable && <TableCell />}
              <TableCell align={"center"}>{"Option"}</TableCell>
              <TableCell align={"right"}>{"Limit"}</TableCell>
              <TableCell align={"right"}>{"Aggregate limit"}</TableCell>
              <TableCell align={"right"}>{"Deductible"}</TableCell>
              {hasPricing && <TableCell align={"right"}>{"Price"}</TableCell>}
              {isFormsAdmin && !isBound && <TableCell />}
              {isBound && <TableCell />}
            </TableRow>
          </TableHead>
          <TableBody>
            {memoisedLayers.map((layer, index) => {
              const selected = selectable && layer.id === selectedLayerId;
              const referralInfo = referralReasons[layer.id] ?? [];

              return (
                <TableRow
                  key={index}
                  sx={{
                    background: selected ? theme.brand.accent[200] : null,
                    cursor: selectable ? "pointer" : null,
                  }}
                  onClick={(_) => updateLayerId(layer.id)}
                >
                  {selectable && (
                    <TableCell>
                      <Checkbox checked={layer.id === selectedLayerId} />
                    </TableCell>
                  )}
                  <TableCell align={"center"}>{index + 1}</TableCell>
                  <TableCell align={"right"}>
                    {currencyFormat(layer.limit) || "-"}
                  </TableCell>
                  <TableCell align={"right"}>
                    {currencyFormat(layer.aggregate) || "-"}
                  </TableCell>
                  <TableCell align={"right"}>
                    {currencyFormat(layer.deductible) || "-"}
                  </TableCell>
                  {hasPricing && (
                    <TableCell align={"right"}>
                      {layer.premium == null
                        ? "Referred"
                        : dpFormatter(layer.premium)}
                    </TableCell>
                  )}
                  {isFormsAdmin &&
                    !isBound &&
                    (referralInfo.length > 0 ? (
                      <TableCell>
                        <Tooltip
                          title={
                            <List>
                              {referralInfo.map((reason, index) => (
                                <ListItem key={index}>{reason}</ListItem>
                              ))}
                            </List>
                          }
                          arrow
                        >
                          <WarningIcon color={"error"} />
                        </Tooltip>
                      </TableCell>
                    ) : (
                      <TableCell />
                    ))}
                  {isBound && (
                    <TableCell>
                      {layer.id === boundLayerId ? (
                        <StatusBadge status={"BOUND"} variant={"chip"} />
                      ) : (
                        <></>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </LabelledElement>
  );
};

export default LayersSummary;
